import {
	Activity,
	AlertCircle,
	AlertTriangle,
	ArrowLeft,
	Calendar,
	CheckCircle,
	Clock,
	Database,
	Edit,
	FileText,
	Globe,
	Info,
	Pause,
	Play,
	RefreshCw,
	RotateCcw,
	Save,
	Webhook,
	XCircle,
} from 'lucide-react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useDialog } from '../components/DialogProvider';
import { useAuth } from '../contexts/AuthContext';
import { api } from '../lib/api-client';
import type {
	ApiError,
	EditSubmissionRequest,
	ExecutionDetail,
	RetryExecutionRequest,
} from '../types/api';

/**
 * Props for the ExecutionDetail component
 */
interface ExecutionDetailProps {}

/**
 * State for managing loading states
 */
interface LoadingState {
	execution: boolean;
	action: string | null;
}

/**
 * State for managing tabs
 */
type TabType = 'dag' | 'trigger-data' | 'logs';

/**
 * Trigger information for execution context
 */
interface TriggerInfo {
	type: 'form' | 'webhook' | 'http-endpoint' | 'schedule' | 'unknown';
	hasInputData: boolean;
	tabLabel: string;
	tabIcon: string;
}

/**
 * Main ExecutionDetail component for displaying detailed execution information
 */
export function ExecutionDetail({}: ExecutionDetailProps) {
	const { confirm } = useDialog();
	const { canExecute, hasAnyRole } = useAuth();
	const navigate = useNavigate();
	const { id } = useParams<{ id: string }>();
	const [execution, setExecution] = useState<ExecutionDetail | null>(null);
	const [loading, setLoading] = useState<LoadingState>({
		execution: true,
		action: null,
	});
	const [error, setError] = useState<string | null>(null);
	const [activeTab, setActiveTab] = useState<TabType>('dag');
	const [editingSubmission, setEditingSubmission] = useState(false);
	const [submissionData, setSubmissionData] = useState('');
	const [isPolling, setIsPolling] = useState(false);
	const [isRefreshing, setIsRefreshing] = useState(false);
	const pollingIntervalRef = useRef<NodeJS.Timeout | null>(null);

	/**
	 * Get trigger information for the execution
	 */
	const getTriggerInfo = useCallback((execution: ExecutionDetail | null): TriggerInfo => {
		const defaultInfo: TriggerInfo = {
			type: 'unknown',
			hasInputData: false,
			tabLabel: 'Trigger Data',
			tabIcon: 'Database',
		};

		if (!execution?.flow?.dagDefinition?.nodes) {
			return defaultInfo;
		}

		// Find the start node
		const startNode = execution.flow.dagDefinition.nodes.find(node =>
			node.type.startsWith('start.')
		);

		// Also check for legacy submissionForm
		const hasSubmissionForm = !!execution.flow.submissionForm;

		if (startNode?.type === 'start.form' || hasSubmissionForm) {
			return {
				type: 'form',
				hasInputData: true,
				tabLabel: 'Submission Data',
				tabIcon: 'FileText',
			};
		}

		if (startNode?.type === 'start.webhook') {
			return {
				type: 'webhook',
				hasInputData: true,
				tabLabel: 'Webhook Payload',
				tabIcon: 'Webhook',
			};
		}

		if (startNode?.type === 'start.http-endpoint') {
			return {
				type: 'http-endpoint',
				hasInputData: true,
				tabLabel: 'Request Data',
				tabIcon: 'Globe',
			};
		}

		if (startNode?.type === 'start.schedule') {
			return {
				type: 'schedule',
				hasInputData: true, // Show schedule config and trigger time
				tabLabel: 'Schedule Info',
				tabIcon: 'Clock',
			};
		}

		return defaultInfo;
	}, []);

	/**
	 * Load execution details from the API
	 */
	const loadExecution = useCallback(
		async (isManualRefresh = false) => {
			if (!id) return;

			try {
				if (isManualRefresh) {
					setIsRefreshing(true);
				} else {
					setLoading(prev => ({ ...prev, execution: true }));
				}
				setError(null);
				const executionData = await api.executions.getExecution(id);
				setExecution(executionData);
				setSubmissionData(JSON.stringify(executionData.submissionData || {}, null, 2));

				// If trigger-data tab is active but execution doesn't have input data, switch to dag tab
				const triggerInfo = getTriggerInfo(executionData);
				if (activeTab === 'trigger-data' && !triggerInfo.hasInputData) {
					setActiveTab('dag');
				}
			} catch (err) {
				const apiError = err as ApiError;
				setError(apiError.message || 'Failed to load execution details');
			} finally {
				if (isManualRefresh) {
					setIsRefreshing(false);
				} else {
					setLoading(prev => ({ ...prev, execution: false }));
				}
			}
		},
		[id, activeTab, getTriggerInfo]
	);

	/**
	 * Manual refresh handler
	 */
	const handleRefresh = useCallback(() => {
		loadExecution(true);
	}, [loadExecution]);

	/**
	 * Check if execution is in a running state that should be polled
	 */
	const shouldPoll = useCallback((execution: ExecutionDetail | null) => {
		return execution && (execution.status === 'running' || execution.status === 'pending');
	}, []);

	/**
	 * Start polling for execution updates
	 */
	const startPolling = useCallback(() => {
		if (pollingIntervalRef.current) return; // Already polling

		setIsPolling(true);
		pollingIntervalRef.current = setInterval(() => {
			// Only poll if tab is visible to save resources
			if (!document.hidden) {
				loadExecution();
			}
		}, 4000); // Poll every 4 seconds
	}, [loadExecution]);

	/**
	 * Stop polling for execution updates
	 */
	const stopPolling = useCallback(() => {
		if (pollingIntervalRef.current) {
			clearInterval(pollingIntervalRef.current);
			pollingIntervalRef.current = null;
			setIsPolling(false);
		}
	}, []);

	/**
	 * Handle execution retry
	 */
	const handleRetry = async () => {
		if (!execution) return;

		confirm({
			title: 'Retry Execution',
			message: 'Are you sure you want to retry this execution?',
			confirmText: 'Retry',
			cancelText: 'Cancel',
			type: 'warning',
			onConfirm: async () => {
				try {
					setLoading(prev => ({ ...prev, action: 'retry' }));
					const request: RetryExecutionRequest = {};
					const newExecution = await api.executions.retryExecution(execution.id, request);
					// Navigate to the new execution instead of refreshing current page
					navigate(`/executions/${newExecution.id}`);
				} catch (err) {
					const apiError = err as ApiError;
					console.error('Failed to retry execution:', apiError);
					setError(apiError.message || 'Failed to retry execution');
				} finally {
					setLoading(prev => ({ ...prev, action: null }));
				}
			},
		});
	};

	/**
	 * Handle editing submission data
	 */
	const handleEditSubmission = async () => {
		if (!execution) return;

		try {
			setLoading(prev => ({ ...prev, action: 'edit-submission' }));
			const parsedData = JSON.parse(submissionData);
			const request: EditSubmissionRequest = {
				submissionData: parsedData,
			};
			await api.executions.editSubmissionData(execution.id, request);
			await loadExecution(); // Refresh the data
			setEditingSubmission(false);
		} catch (err) {
			if (err instanceof SyntaxError) {
				setError('Invalid JSON format in submission data');
			} else {
				const apiError = err as ApiError;
				setError(apiError.message || 'Failed to update submission data');
			}
		} finally {
			setLoading(prev => ({ ...prev, action: null }));
		}
	};

	/**
	 * Handle canceling edit
	 */
	const handleCancelEdit = () => {
		setEditingSubmission(false);
		if (execution) {
			setSubmissionData(JSON.stringify(execution.submissionData || {}, null, 2));
		}
		setError(null);
	};

	/**
	 * Get status icon for execution
	 */
	const getStatusIcon = (status: ExecutionDetail['status']) => {
		const iconClasses = 'w-5 h-5';
		switch (status) {
			case 'completed':
				return <CheckCircle className={`${iconClasses} text-green-600`} />;
			case 'failed':
				return <XCircle className={`${iconClasses} text-red-600`} />;
			case 'running':
				return <Play className={`${iconClasses} text-blue-600`} />;
			case 'pending':
				return <Clock className={`${iconClasses} text-yellow-600`} />;
			case 'cancelled':
				return <Pause className={`${iconClasses} text-gray-600`} />;
			default:
				return <AlertCircle className={`${iconClasses} text-gray-600`} />;
		}
	};

	/**
	 * Get status badge styling
	 */
	const getStatusBadge = (status: ExecutionDetail['status']) => {
		const baseClasses =
			'inline-flex items-center px-3 py-1 text-sm font-medium rounded-full gap-2';
		switch (status) {
			case 'completed':
				return `${baseClasses} bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-400`;
			case 'failed':
				return `${baseClasses} bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-400`;
			case 'running':
				return `${baseClasses} bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-400`;
			case 'pending':
				return `${baseClasses} bg-yellow-100 dark:bg-yellow-900/30 text-yellow-800 dark:text-yellow-400`;
			case 'cancelled':
				return `${baseClasses} bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-300`;
			default:
				return `${baseClasses} bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-300`;
		}
	};

	/**
	 * Get log level icon
	 */
	const getLogLevelIcon = (level: string) => {
		const iconClasses = 'w-4 h-4';
		switch (level) {
			case 'error':
				return <XCircle className={`${iconClasses} text-red-500`} />;
			case 'warn':
				return <AlertTriangle className={`${iconClasses} text-yellow-500`} />;
			case 'info':
				return <Info className={`${iconClasses} text-blue-500`} />;
			case 'debug':
				return <Activity className={`${iconClasses} text-gray-500`} />;
			default:
				return <Info className={`${iconClasses} text-gray-500`} />;
		}
	};

	/**
	 * Get node status styling
	 */
	const getNodeStatusStyle = (status: string) => {
		switch (status) {
			case 'completed':
				return 'border-green-500 dark:border-green-400 bg-green-50 dark:bg-green-900/30 text-green-800 dark:text-green-300';
			case 'failed':
				return 'border-red-500 dark:border-red-400 bg-red-50 dark:bg-red-900/30 text-red-800 dark:text-red-300';
			case 'running':
				return 'border-blue-500 dark:border-blue-400 bg-blue-50 dark:bg-blue-900/30 text-blue-800 dark:text-blue-300';
			case 'pending':
				return 'border-yellow-500 dark:border-yellow-400 bg-yellow-50 dark:bg-yellow-900/30 text-yellow-800 dark:text-yellow-300';
			case 'skipped':
				return 'border-gray-300 dark:border-gray-600 bg-gray-50 dark:bg-gray-800 text-gray-600 dark:text-gray-400';
			default:
				return 'border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-600 dark:text-gray-400';
		}
	};

	/**
	 * Format date for display
	 */
	const formatDate = (dateString: string) => {
		return new Date(dateString).toLocaleDateString('en-US', {
			year: 'numeric',
			month: 'short',
			day: 'numeric',
			hour: '2-digit',
			minute: '2-digit',
			second: '2-digit',
		});
	};

	/**
	 * Calculate duration
	 */
	const getDuration = (startedAt: string, completedAt?: string) => {
		const start = new Date(startedAt);
		const end = completedAt ? new Date(completedAt) : new Date();
		const diffMs = end.getTime() - start.getTime();

		const seconds = Math.floor(diffMs / 1000);
		const minutes = Math.floor(seconds / 60);
		const hours = Math.floor(minutes / 60);

		if (hours > 0) {
			return `${hours}h ${minutes % 60}m ${seconds % 60}s`;
		} else if (minutes > 0) {
			return `${minutes}m ${seconds % 60}s`;
		} else {
			return `${seconds}s`;
		}
	};

	// Load execution on component mount
	useEffect(() => {
		loadExecution();
	}, [loadExecution]);

	// Handle polling based on execution status
	useEffect(() => {
		if (shouldPoll(execution)) {
			startPolling();
		} else {
			stopPolling();
		}
	}, [execution, shouldPoll, startPolling, stopPolling]);

	// Cleanup polling on unmount
	useEffect(() => {
		return () => {
			stopPolling();
		};
	}, [stopPolling]);

	// Handle visibility change to pause/resume polling
	useEffect(() => {
		const handleVisibilityChange = () => {
			if (document.hidden) {
				// Tab became hidden, polling will pause automatically
			} else if (shouldPoll(execution)) {
				// Tab became visible and we should be polling
				if (!pollingIntervalRef.current) {
					startPolling();
				}
			}
		};

		document.addEventListener('visibilitychange', handleVisibilityChange);
		return () => {
			document.removeEventListener('visibilitychange', handleVisibilityChange);
		};
	}, [execution, shouldPoll, startPolling]);

	if (loading.execution) {
		return (
			<div className="w-full px-4 py-8 sm:px-6 lg:px-8">
				<div className="text-center py-12">
					<div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
					<p className="mt-2 text-gray-600 dark:text-gray-400">
						Loading execution details...
					</p>
				</div>
			</div>
		);
	}

	if (error || !execution) {
		return (
			<div className="w-full px-4 py-8 sm:px-6 lg:px-8">
				<div className="text-center py-12">
					<XCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
					<h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
						Failed to load execution
					</h3>
					<p className="text-gray-600 dark:text-gray-400 mb-4">
						{error || 'Execution not found'}
					</p>
					<Link
						to="/executions"
						className="inline-flex items-center text-blue-600 hover:text-blue-900 dark:text-blue-400 dark:hover:text-blue-300"
					>
						<ArrowLeft className="w-4 h-4 mr-1" />
						Back to Executions
					</Link>
				</div>
			</div>
		);
	}

	return (
		<div className="w-full px-4 py-8 sm:px-6 lg:px-8">
			{/* Header */}
			<div className="mb-8">
				<div className="flex items-center gap-4 mb-4">
					<Link
						to="/executions"
						className="inline-flex items-center text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-white"
					>
						<ArrowLeft className="w-5 h-5 mr-1" />
						Back to Executions
					</Link>
				</div>

				<div className="flex justify-between items-start">
					<div>
						<h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">
							Execution Details
						</h1>
						<div className="flex items-center gap-4 text-sm text-gray-600 dark:text-gray-400">
							<span>ID: {execution.id}</span>
							<span>•</span>
							<span>Flow: {execution.flowName}</span>
							<span>•</span>
							<span>Version: v{execution.flowVersion}</span>
						</div>
					</div>

					<div className="flex items-center gap-3">
						{/* Refresh Button */}
						<button
							onClick={handleRefresh}
							disabled={isRefreshing || loading.execution}
							className="inline-flex items-center px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
							title={
								isPolling ? 'Auto-refreshing every 4s' : 'Refresh execution details'
							}
						>
							{isRefreshing ? (
								<div className="animate-spin w-4 h-4 border-2 border-gray-400 border-t-transparent rounded-full mr-2" />
							) : (
								<RefreshCw
									className={`w-4 h-4 mr-2 ${isPolling ? 'animate-pulse' : ''}`}
								/>
							)}
							Refresh
							{isPolling && (
								<span className="ml-2 px-1.5 py-0.5 text-xs bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-400 rounded">
									Auto
								</span>
							)}
						</button>

						{/* Retry Button - EXECUTE+ or SUPPORT+ users */}
						{(canExecute() || hasAnyRole(['SUPPORT', 'ADMIN'])) &&
							(() => {
								const isCompleted = execution.status === 'completed';
								const isFailedOrCancelled =
									execution.status === 'failed' ||
									execution.status === 'cancelled';
								const isRunningOrPending =
									execution.status === 'running' ||
									execution.status === 'pending';

								// Only show button for failed/cancelled executions
								if (!isFailedOrCancelled) {
									return null;
								}

								const isDisabled = loading.action === 'retry';
								const tooltipMessage = isDisabled ? 'Retrying execution...' : '';

								return (
									<button
										onClick={handleRetry}
										disabled={isDisabled}
										title={tooltipMessage}
										className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
									>
										{loading.action === 'retry' ? (
											<div className="animate-spin w-4 h-4 border-2 border-white border-t-transparent rounded-full mr-2" />
										) : (
											<RotateCcw className="w-4 h-4 mr-2" />
										)}
										Retry Execution
									</button>
								);
							})()}
					</div>
				</div>

				{/* Status and Timing */}
				<div className="mt-6 grid grid-cols-1 md:grid-cols-4 gap-6">
					<div className="bg-white dark:bg-gray-800 p-4 rounded-lg border border-gray-200 dark:border-gray-700">
						<div className="flex items-center">
							<div className="flex-shrink-0">{getStatusIcon(execution.status)}</div>
							<div className="ml-3">
								<p className="text-sm font-medium text-gray-900 dark:text-white">
									Status
								</p>
								<span className={getStatusBadge(execution.status)}>
									{execution.status}
								</span>
							</div>
						</div>
					</div>

					<div className="bg-white dark:bg-gray-800 p-4 rounded-lg border border-gray-200 dark:border-gray-700">
						<div className="flex items-center">
							<Calendar className="w-5 h-5 text-gray-400 dark:text-gray-500" />
							<div className="ml-3">
								<p className="text-sm font-medium text-gray-900 dark:text-white">
									Started
								</p>
								<p className="text-sm text-gray-600 dark:text-gray-400">
									{formatDate(execution.startedAt)}
								</p>
							</div>
						</div>
					</div>

					{execution.completedAt && (
						<div className="bg-white dark:bg-gray-800 p-4 rounded-lg border border-gray-200 dark:border-gray-700">
							<div className="flex items-center">
								<Calendar className="w-5 h-5 text-gray-400 dark:text-gray-500" />
								<div className="ml-3">
									<p className="text-sm font-medium text-gray-900 dark:text-white">
										Completed
									</p>
									<p className="text-sm text-gray-600 dark:text-gray-400">
										{formatDate(execution.completedAt)}
									</p>
								</div>
							</div>
						</div>
					)}

					<div className="bg-white dark:bg-gray-800 p-4 rounded-lg border border-gray-200 dark:border-gray-700">
						<div className="flex items-center">
							<Clock className="w-5 h-5 text-gray-400 dark:text-gray-500" />
							<div className="ml-3">
								<p className="text-sm font-medium text-gray-900 dark:text-white">
									Duration
								</p>
								<p className="text-sm text-gray-600 dark:text-gray-400">
									{getDuration(execution.startedAt, execution.completedAt)}
								</p>
							</div>
						</div>
					</div>
				</div>
			</div>

			{/* Error Message */}
			{error && (
				<div className="mb-6 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-md">
					<p className="text-red-800 dark:text-red-400">{error}</p>
				</div>
			)}

			{/* Tabs */}
			<div className="mb-6">
				<div className="border-b border-gray-200 dark:border-gray-700">
					<nav className="-mb-px flex space-x-8">
						<button
							onClick={() => setActiveTab('dag')}
							className={`py-2 px-1 border-b-2 font-medium text-sm ${
								activeTab === 'dag'
									? 'border-blue-500 text-blue-600 dark:text-blue-400'
									: 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 hover:border-gray-300 dark:hover:border-gray-600'
							}`}
						>
							<Activity className="w-4 h-4 inline mr-2" />
							Workflow Progress
						</button>
						{/* Dynamically show trigger data tab based on trigger type */}
						{(() => {
							const triggerInfo = getTriggerInfo(execution);
							if (!triggerInfo.hasInputData) return null;

							// Get the appropriate icon component
							const IconComponent =
								triggerInfo.tabIcon === 'Webhook'
									? Webhook
									: triggerInfo.tabIcon === 'Globe'
									? Globe
									: triggerInfo.tabIcon === 'Clock'
									? Clock
									: triggerInfo.tabIcon === 'FileText'
									? FileText
									: Database;

							return (
								<button
									onClick={() => setActiveTab('trigger-data')}
									className={`py-2 px-1 border-b-2 font-medium text-sm ${
										activeTab === 'trigger-data'
											? 'border-blue-500 text-blue-600 dark:text-blue-400'
											: 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 hover:border-gray-300 dark:hover:border-gray-600'
									}`}
								>
									<IconComponent className="w-4 h-4 inline mr-2" />
									{triggerInfo.tabLabel}
								</button>
							);
						})()}
						<button
							onClick={() => setActiveTab('logs')}
							className={`py-2 px-1 border-b-2 font-medium text-sm ${
								activeTab === 'logs'
									? 'border-blue-500 text-blue-600 dark:text-blue-400'
									: 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 hover:border-gray-300 dark:hover:border-gray-600'
							}`}
						>
							<FileText className="w-4 h-4 inline mr-2" />
							Execution Logs
						</button>
					</nav>
				</div>
			</div>

			{/* Tab Content */}
			<div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
				{activeTab === 'dag' && (
					<div className="p-6">
						<h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">
							Node Status Overview
						</h3>
						<div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
							{Object.entries(execution.nodeStatuses).map(([nodeId, status]) => (
								<div
									key={nodeId}
									className={`p-4 rounded-lg border-2 ${getNodeStatusStyle(
										status.status
									)}`}
								>
									<div className="flex items-center justify-between mb-2">
										<h4 className="font-medium">{nodeId}</h4>
										<span className="text-xs font-medium px-2 py-1 rounded">
											{status.status}
										</span>
									</div>
									{status.startedAt && (
										<p className="text-xs mb-1 opacity-90">
											Started: {formatDate(status.startedAt)}
										</p>
									)}
									{status.completedAt && (
										<p className="text-xs mb-1 opacity-90">
											Completed: {formatDate(status.completedAt)}
										</p>
									)}
									{status.error && (
										<p className="text-xs text-red-600 dark:text-red-400 mt-2 font-medium">
											Error: {status.error}
										</p>
									)}
								</div>
							))}
						</div>
					</div>
				)}

				{activeTab === 'trigger-data' &&
					(() => {
						const triggerInfo = getTriggerInfo(execution);
						if (!triggerInfo.hasInputData) return null;

						return (
							<div className="p-6">
								<div className="flex items-center justify-between mb-4">
									<h3 className="text-lg font-medium text-gray-900 dark:text-white">
										{triggerInfo.tabLabel}
									</h3>
									{/* Only show Edit & Retry for form submissions */}
									{triggerInfo.type === 'form' &&
										(!editingSubmission &&
										(canExecute() || hasAnyRole(['SUPPORT', 'ADMIN'])) ? (
											<button
												onClick={() => setEditingSubmission(true)}
												className="inline-flex items-center px-3 py-1.5 border border-gray-300 dark:border-gray-600 rounded-md text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600"
											>
												<Edit className="w-4 h-4 mr-1" />
												Edit & Retry
											</button>
										) : !editingSubmission ? null : (
											<div className="flex gap-2">
												<button
													onClick={handleCancelEdit}
													className="inline-flex items-center px-3 py-1.5 border border-gray-300 dark:border-gray-600 rounded-md text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600"
												>
													Cancel
												</button>
												<button
													onClick={handleEditSubmission}
													disabled={loading.action === 'edit-submission'}
													className="inline-flex items-center px-3 py-1.5 border border-transparent rounded-md text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50"
												>
													{loading.action === 'edit-submission' ? (
														<div className="animate-spin w-4 h-4 border-2 border-white border-t-transparent rounded-full mr-1" />
													) : (
														<Save className="w-4 h-4 mr-1" />
													)}
													Save & Retry
												</button>
											</div>
										))}
								</div>

								{editingSubmission ? (
									<textarea
										value={submissionData}
										onChange={e => setSubmissionData(e.target.value)}
										className="w-full h-64 font-mono text-sm border border-gray-300 dark:border-gray-600 rounded-md p-3 bg-white dark:bg-gray-900 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
										placeholder={`Enter JSON ${
											triggerInfo.type === 'webhook'
												? 'webhook payload'
												: triggerInfo.type === 'http-endpoint'
												? 'request data'
												: 'trigger data'
										}`}
									/>
								) : (
									<>
										<pre className="bg-gray-50 dark:bg-gray-900 text-gray-900 dark:text-gray-100 p-4 rounded-lg overflow-auto text-sm font-mono">
											{JSON.stringify(
												execution.submissionData || {},
												null,
												2
											)}
										</pre>
										{triggerInfo.type === 'webhook' && (
											<div className="mt-4 text-xs text-gray-600 dark:text-gray-400">
												<p className="font-medium mb-1">
													💡 This is the payload that was sent to the
													webhook endpoint
												</p>
											</div>
										)}
										{triggerInfo.type === 'http-endpoint' && (
											<div className="mt-4 text-xs text-gray-600 dark:text-gray-400">
												<p className="font-medium mb-1">
													💡 This is the request data sent to the HTTP
													endpoint
												</p>
											</div>
										)}
										{triggerInfo.type === 'schedule' && (
											<div className="mt-4 text-xs text-gray-600 dark:text-gray-400">
												<p className="font-medium mb-1">
													⏰ Scheduled execution triggered at{' '}
													{new Date(execution.startedAt).toLocaleString()}
												</p>
											</div>
										)}
									</>
								)}
							</div>
						);
					})()}

				{activeTab === 'logs' && (
					<div className="p-6">
						<h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">
							Execution Logs
						</h3>
						{execution.logs.length === 0 ? (
							<div className="text-center py-8">
								<FileText className="w-12 h-12 text-gray-400 dark:text-gray-500 mx-auto mb-4" />
								<p className="text-gray-600 dark:text-gray-400">
									No logs available for this execution
								</p>
							</div>
						) : (
							<div className="space-y-3">
								{execution.logs.map(log => (
									<div
										key={log.id}
										className="border border-gray-200 dark:border-gray-700 rounded-lg p-4 hover:bg-gray-50 dark:hover:bg-gray-700/50"
									>
										<div className="flex items-start justify-between mb-2">
											<div className="flex items-center gap-2">
												{getLogLevelIcon(log.level)}
												<span className="font-medium text-sm text-gray-900 dark:text-white">
													{log.nodeName}
												</span>
												<span className="text-xs text-gray-500 dark:text-gray-400">
													({log.nodeId})
												</span>
											</div>
											<span className="text-xs text-gray-500 dark:text-gray-400">
												{formatDate(log.timestamp)}
											</span>
										</div>

										<p className="text-sm text-gray-900 dark:text-gray-100 mb-2">
											{log.message}
										</p>

										{log.input && (
											<details className="mb-2">
												<summary className="text-xs text-gray-600 dark:text-gray-400 cursor-pointer hover:text-gray-900 dark:hover:text-gray-200">
													Input Data
												</summary>
												<pre className="mt-2 text-xs bg-gray-100 dark:bg-gray-800 text-gray-900 dark:text-gray-100 p-2 rounded overflow-auto">
													{JSON.stringify(log.input, null, 2)}
												</pre>
											</details>
										)}

										{log.output && (
											<details className="mb-2">
												<summary className="text-xs text-gray-600 dark:text-gray-400 cursor-pointer hover:text-gray-900 dark:hover:text-gray-200">
													Output Data
												</summary>
												<pre className="mt-2 text-xs bg-gray-100 dark:bg-gray-800 text-gray-900 dark:text-gray-100 p-2 rounded overflow-auto">
													{JSON.stringify(log.output, null, 2)}
												</pre>
											</details>
										)}

										{log.error && (
											<div className="mt-2 p-2 bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-700 rounded">
												<p className="text-xs text-red-800 dark:text-red-400 font-medium">
													Error:
												</p>
												<p className="text-xs text-red-700 dark:text-red-300">
													{log.error}
												</p>
											</div>
										)}

										{/* API Call Details for Failed API Calls */}
										{log.apiCallDetails && (
											<details className="mt-2">
												<summary className="text-xs text-blue-600 dark:text-blue-400 cursor-pointer hover:text-blue-800 dark:hover:text-blue-300 font-medium">
													API Call Details
												</summary>
												<div className="mt-2 space-y-2">
													{/* Endpoint Information */}
													<div className="text-xs">
														<span className="font-medium text-gray-700 dark:text-gray-300">
															Endpoint:
														</span>
														<span className="ml-1 text-gray-600 dark:text-gray-400">
															{log.apiCallDetails.method}{' '}
															{log.apiCallDetails.url}
														</span>
													</div>

													{/* Status Information */}
													{log.apiCallDetails.statusCode && (
														<div className="text-xs">
															<span className="font-medium text-gray-700 dark:text-gray-300">
																Status:
															</span>
															<span className="ml-1 text-gray-600 dark:text-gray-400">
																{log.apiCallDetails.statusCode}{' '}
																{log.apiCallDetails.statusText ||
																	''}
															</span>
														</div>
													)}

													{/* Duration */}
													{log.apiCallDetails.duration && (
														<div className="text-xs">
															<span className="font-medium text-gray-700 dark:text-gray-300">
																Duration:
															</span>
															<span className="ml-1 text-gray-600 dark:text-gray-400">
																{log.apiCallDetails.duration}ms
															</span>
														</div>
													)}

													{/* Request Headers */}
													{log.apiCallDetails.requestHeaders && (
														<details className="mt-1">
															<summary className="text-xs text-gray-600 dark:text-gray-400 cursor-pointer hover:text-gray-800 dark:hover:text-gray-200">
																Request Headers
															</summary>
															<pre className="mt-1 text-xs bg-gray-100 dark:bg-gray-800 text-gray-900 dark:text-gray-100 p-2 rounded overflow-auto max-h-32">
																{JSON.stringify(
																	log.apiCallDetails
																		.requestHeaders,
																	null,
																	2
																)}
															</pre>
														</details>
													)}

													{/* Request Payload */}
													{log.apiCallDetails.requestData && (
														<details className="mt-1">
															<summary className="text-xs text-gray-600 dark:text-gray-400 cursor-pointer hover:text-gray-800 dark:hover:text-gray-200">
																Request Payload
															</summary>
															<pre className="mt-1 text-xs bg-gray-100 dark:bg-gray-800 text-gray-900 dark:text-gray-100 p-2 rounded overflow-auto max-h-32">
																{JSON.stringify(
																	log.apiCallDetails.requestData,
																	null,
																	2
																)}
															</pre>
														</details>
													)}

													{/* Response Data (if available) */}
													{log.apiCallDetails.responseData && (
														<details className="mt-1">
															<summary className="text-xs text-gray-600 dark:text-gray-400 cursor-pointer hover:text-gray-800 dark:hover:text-gray-200">
																Response Data
															</summary>
															<pre className="mt-1 text-xs bg-gray-100 dark:bg-gray-800 text-gray-900 dark:text-gray-100 p-2 rounded overflow-auto max-h-32">
																{JSON.stringify(
																	log.apiCallDetails.responseData,
																	null,
																	2
																)}
															</pre>
														</details>
													)}

													{/* Error Code */}
													{log.apiCallDetails.errorCode && (
														<div className="text-xs">
															<span className="font-medium text-gray-700 dark:text-gray-300">
																Error Code:
															</span>
															<span className="ml-1 text-gray-600 dark:text-gray-400">
																{log.apiCallDetails.errorCode}
															</span>
														</div>
													)}

													{/* Retryable Status */}
													{log.apiCallDetails.isRetryable !==
														undefined && (
														<div className="text-xs">
															<span className="font-medium text-gray-700 dark:text-gray-300">
																Retryable:
															</span>
															<span
																className={`ml-1 ${
																	log.apiCallDetails.isRetryable
																		? 'text-green-600 dark:text-green-400'
																		: 'text-red-600 dark:text-red-400'
																}`}
															>
																{log.apiCallDetails.isRetryable
																	? 'Yes'
																	: 'No'}
															</span>
														</div>
													)}
												</div>
											</details>
										)}
									</div>
								))}
							</div>
						)}
					</div>
				)}
			</div>
		</div>
	);
}
